import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { FileText, Plus, Edit2, Trash2, Save, X, Eye, Bold, Italic, List, ListOrdered, Type } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPages = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    meta_description: '',
    enabled: true
  });
  const contentEditableRef = useRef(null);

  // Format functions for the editor
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
  };

  const formatBold = () => execCommand('bold');
  const formatItalic = () => execCommand('italic');
  const formatUnderline = () => execCommand('underline');
  const formatOrderedList = () => execCommand('insertOrderedList');
  const formatUnorderedList = () => execCommand('insertUnorderedList');
  const formatHeading = (level) => execCommand('formatBlock', `<h${level}>`);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pages`);
      const data = await response.json();
      setPages(data);
    } catch (error) {
      console.error('Sayfa yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    const turkishMap = {
      'ç': 'c', 'ğ': 'g', 'ı': 'i', 'ö': 'o', 'ş': 's', 'ü': 'u',
      'Ç': 'c', 'Ğ': 'g', 'İ': 'i', 'Ö': 'o', 'Ş': 's', 'Ü': 'u'
    };
    
    return title
      .split('')
      .map(char => turkishMap[char] || char)
      .join('')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (value) => {
    setFormData({
      ...formData,
      title: value,
      slug: editingPage ? formData.slug : generateSlug(value)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get HTML content from contenteditable
    const htmlContent = contentEditableRef.current ? contentEditableRef.current.innerHTML : formData.content;
    
    try {
      const token = localStorage.getItem('adminToken');
      const url = editingPage 
        ? `${API_URL}/api/admin/pages/${editingPage.id}`
        : `${API_URL}/api/admin/pages`;
      
      const method = editingPage ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          content: htmlContent
        })
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (response.ok) {
        alert(`✅ Sayfa başarıyla ${editingPage ? 'güncellendi' : 'oluşturuldu'}!`);
        setShowModal(false);
        resetForm();
        fetchPages();
      } else {
        const error = await response.json();
        alert('❌ Hata: ' + (error.detail || 'İşlem başarısız'));
      }
    } catch (error) {
      console.error('Kayıt hatası:', error);
      alert('❌ Bir hata oluştu!');
    }
  };

  const handleDelete = async (page) => {
    if (!window.confirm(`"${page.title}" sayfasını silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/pages/${page.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (response.ok) {
        alert('✅ Sayfa başarıyla silindi!');
        fetchPages();
      } else {
        const error = await response.json();
        alert('❌ Hata: ' + (error.detail || 'Sayfa silinemedi'));
      }
    } catch (error) {
      console.error('Silme hatası:', error);
      alert('❌ Bir hata oluştu!');
    }
  };

  const openEditModal = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title,
      slug: page.slug,
      content: page.content || '',
      meta_description: page.meta_description || '',
      enabled: page.enabled !== false
    });
    setShowModal(true);
    // Set content to contenteditable after modal opens
    setTimeout(() => {
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = page.content || '';
      }
    }, 100);
  };

  const openCreateModal = () => {
    setEditingPage(null);
    resetForm();
    setShowModal(true);
    setTimeout(() => {
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = '';
      }
    }, 100);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      meta_description: '',
      enabled: true
    });
    if (contentEditableRef.current) {
      contentEditableRef.current.innerHTML = '';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Sayfa Yönetimi</h1>
            <p className="text-gray-600 mt-1">Dinamik sayfaları oluşturun ve düzenleyin</p>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
          >
            <Plus size={20} />
            Yeni Sayfa
          </button>
        </div>

        {/* Pages Grid */}
        {pages.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FileText size={64} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Henüz sayfa yok</h2>
            <p className="text-gray-500 mb-6">İlk sayfanızı oluşturarak başlayın</p>
            <button
              onClick={openCreateModal}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Yeni Sayfa Oluştur
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pages.map((page) => (
              <div key={page.id} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1">{page.title}</h3>
                    <p className="text-sm text-gray-500">/{page.slug}</p>
                  </div>
                  {!page.enabled && (
                    <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">
                      Pasif
                    </span>
                  )}
                </div>

                {page.meta_description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {page.meta_description}
                  </p>
                )}

                <div className="flex items-center gap-2 pt-4 border-t">
                  <a
                    href={`/${page.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                  >
                    <Eye size={16} />
                    Görüntüle
                  </a>
                  <button
                    onClick={() => openEditModal(page)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 transition-colors text-sm"
                  >
                    <Edit2 size={16} />
                    Düzenle
                  </button>
                  <button
                    onClick={() => handleDelete(page)}
                    className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingPage ? 'Sayfa Düzenle' : 'Yeni Sayfa Oluştur'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                    setEditingPage(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sayfa Başlığı *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      required
                      placeholder="örn: Hakkımızda"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL Slug *
                    </label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      required
                      placeholder="örn: about"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Sayfa URL'i: /{formData.slug}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sayfa İçeriği
                  </label>
                  
                  {/* Formatting Toolbar */}
                  <div className="bg-gray-50 border border-gray-300 rounded-t-lg p-2 flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => formatHeading(2)}
                      className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm font-semibold"
                      title="Başlık"
                    >
                      H2
                    </button>
                    <button
                      type="button"
                      onClick={() => formatHeading(3)}
                      className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100 text-sm font-semibold"
                      title="Alt Başlık"
                    >
                      H3
                    </button>
                    <div className="w-px bg-gray-300 mx-1"></div>
                    <button
                      type="button"
                      onClick={formatBold}
                      className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Kalın"
                    >
                      <Bold size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={formatItalic}
                      className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="İtalik"
                    >
                      <Italic size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={formatUnderline}
                      className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Alt Çizgi"
                    >
                      <Type size={16} />
                    </button>
                    <div className="w-px bg-gray-300 mx-1"></div>
                    <button
                      type="button"
                      onClick={formatUnorderedList}
                      className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Liste"
                    >
                      <List size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={formatOrderedList}
                      className="px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-100"
                      title="Numaralı Liste"
                    >
                      <ListOrdered size={16} />
                    </button>
                  </div>
                  
                  {/* ContentEditable Area */}
                  <div
                    ref={contentEditableRef}
                    contentEditable
                    className="w-full min-h-[400px] px-4 py-3 border border-t-0 border-gray-300 rounded-b-lg focus:outline-none focus:ring-2 focus:ring-red-600 bg-white prose max-w-none"
                    style={{ 
                      maxHeight: '500px',
                      overflowY: 'auto'
                    }}
                    suppressContentEditableWarning
                  />
                  
                  <p className="text-xs text-gray-500 mt-2">
                    💡 İpucu: Metni seçip üstteki butonlarla biçimlendirebilirsiniz.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meta Açıklama (SEO)
                  </label>
                  <textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                    rows={2}
                    placeholder="Sayfa hakkında kısa açıklama (Google'da görünür)"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="enabled"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    className="w-4 h-4 text-red-600 rounded focus:ring-red-600"
                  />
                  <label htmlFor="enabled" className="text-sm text-gray-700">
                    Sayfayı Aktif Et
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                  >
                    <Save size={18} />
                    {editingPage ? 'Güncelle' : 'Oluştur'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                      setEditingPage(null);
                    }}
                    className="px-8 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    İptal
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPages;
